// Central consent + gtag loader with Google Consent Mode v2.
//
// Consent Mode v2 lädt gtag SOFORT auf jedem Seitenbesuch mit
// `default: denied` für alle relevanten Kategorien.  Wenn der User
// ablehnt: Google bekommt trotzdem *cookieless pings* (ohne
// personenbezogene Daten) und modelliert daraus statistische
// Conversions.  Wenn der User zustimmt: `consent update` schaltet auf
// `granted` — ab da läuft klassisches Tracking mit Cookies.
//
// Ergebnis: DSGVO konform + typisch +15 bis +30 % mehr sichtbare
// Conversions in Google Ads durch modellierte Daten.
//
// Consent wird in localStorage unter `cre8_cookie_consent_v2` abgelegt:
//   { necessary: true, marketing: boolean, ts: string }

export const STORAGE_KEY = "cre8_cookie_consent_v2";
const GTAG_ID = "AW-18361166788";
const CONSENT_EVENT = "cre8-consent-change";

export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setConsent(next) {
  const value = { necessary: true, ...next, ts: new Date().toISOString() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  // Consent Mode v2 Update Signal an Google
  applyConsentToGtag(value);
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
  return value;
}

export function clearConsent() {
  localStorage.removeItem(STORAGE_KEY);
  applyConsentToGtag(null); // → alles auf denied zurück
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: null }));
}

// Injects gtag.js exactly once. Idempotent.
function loadGtagScript() {
  if (typeof window === "undefined") return;
  if (window.__cre8GtagLoaded) return;
  window.__cre8GtagLoaded = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };

  // 1) Consent Mode v2 Default Signal MUSS vor dem ersten config Aufruf
  //    kommen.  Wir starten mit denied und upgraden später wenn nötig.
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    wait_for_update: 500,
  });

  window.gtag("js", new Date());
  // url_passthrough hilft Google Ads Attribution zu erhalten wenn Cookies
  // (noch) nicht gesetzt sind — trägt gclid als URL Parameter weiter.
  window.gtag("config", GTAG_ID, {
    url_passthrough: true,
    ads_data_redaction: true,
  });

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`;
  s.setAttribute("data-cre8-gtag", "true");
  document.head.appendChild(s);
}

// Wendet den aktuellen Consent Zustand als gtag `consent update` an.
// Wird bei jeder Änderung und beim App Start aufgerufen.
function applyConsentToGtag(consent) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const granted = !!consent?.marketing;
  window.gtag("consent", "update", {
    ad_storage: granted ? "granted" : "denied",
    ad_user_data: granted ? "granted" : "denied",
    ad_personalization: granted ? "granted" : "denied",
    analytics_storage: granted ? "granted" : "denied",
  });
}

// Backwards compatibility export: früher hat der CookieBanner direkt
// loadGtag() aufgerufen.  Behalten wir als no op weil gtag jetzt ohnehin
// beim App Start geladen wird.
export function loadGtag() {
  loadGtagScript();
}

// Wird beim App Start (App.js) einmal aufgerufen. Lädt gtag mit
// denied default und upgradet direkt wenn schon Zustimmung vorliegt.
export function initConsent() {
  loadGtagScript();
  const c = getConsent();
  if (c) applyConsentToGtag(c);
}

/**
 * Feuert ein Google Ads Conversion Event.  Mit Consent Mode v2 rufen
 * wir das Event IMMER auf — Google entscheidet basierend auf dem
 * aktuellen Consent Zustand ob mit Cookies (granted) oder als
 * cookieless ping mit anschließender Modellierung (denied) getrackt
 * wird.  So bekommst du auch bei abgelehnter Zustimmung modellierte
 * Conversions in Google Ads.
 * `sendTo` ist das AW-account/label Paar (z.B. "AW-18361166788/xxx").
 */
export function trackConversion(sendTo, extra = {}) {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") {
    // gtag noch nicht geladen — nachladen und Event kurz danach feuern
    loadGtagScript();
    setTimeout(() => trackConversion(sendTo, extra), 400);
    return;
  }
  window.gtag("event", "conversion", { send_to: sendTo, ...extra });
}
